<template>
  <section class="review-demo" aria-label="Interactive report review" @keydown.esc="leaveDemo">
    <div class="rw-demo-stage" :class="{ 'is-active': demoActive }">
    <div class="rw-shell" :inert="!demoActive">
      <header class="rw-header">
        <div><SiteIcon name="document" /><strong>Report review</strong></div>
        <span>Synthetic example</span>
      </header>

      <header v-if="exploring" class="rw-explorer-header">
        <button ref="backToFindings" class="rw-text-button" type="button" @click="closeExplorer"><SiteIcon name="arrow-left" />Back to findings</button>
        <h3>Evidence explorer</h3>
      </header>

      <div class="rw-workspace" :class="{ 'is-exploring': exploring }">
        <aside class="rw-sidebar" :aria-label="exploring ? 'Review areas' : 'Report findings'">
          <template v-if="!exploring">
            <div class="rw-report-heading">
              <h3>Quarterly review</h3>
              <p>April–June 2026 <span>8 pages</span></p>
              <button ref="explorerEntry" type="button" class="rw-explorer-entry" @click="openExplorer"><SiteIcon name="window" />Evidence explorer<ArrowUpRightIcon /></button>
            </div>
            <div class="rw-list-label"><strong>Findings {{ findings.length }}</strong><span>{{ counts.reviewed }} reviewed</span></div>
            <nav class="rw-finding-list" aria-label="Findings">
              <button v-for="item in findings" :key="item.id" class="rw-finding" type="button" :aria-pressed="selectedId === item.id" @click="selectFinding(item.id)">
                <span class="rw-finding-meta"><b>{{ item.code }}</b><span>{{ statusFor(item.id) }}</span></span>
                <span>{{ item.title }}</span>
              </button>
            </nav>
            <details class="rw-no-findings">
              <summary>Areas with no findings <span>1</span></summary>
              <button class="rw-finding" type="button" :aria-pressed="selectedId === 'c'" @click="selectFinding('c')"><span class="rw-finding-meta"><b>C</b><span>No findings</span></span>Supporting references</button>
            </details>
            <div class="rw-sidebar-footer">
              <button class="rw-button" type="button" @click="openDialog('summary')"><SiteIcon name="document" />Review summary</button>
              <button class="rw-button" type="button" :disabled="counts.reviewed !== counts.total || completed" @click="completeReview"><SiteIcon name="check" />{{ completed ? 'Review completed' : 'Complete review' }}</button>
            </div>
          </template>
          <template v-else>
            <div class="rw-list-label"><strong>Review areas</strong></div>
            <nav class="rw-area-list" aria-label="Review areas">
              <button v-for="area in areas" :key="area.id" class="rw-finding" type="button" :aria-pressed="explorerId === area.id" @click="selectArea(area.id)"><b>{{ area.code }}</b><span>{{ area.label }}</span></button>
            </nav>
          </template>
        </aside>

        <section v-if="!exploring" class="rw-reading" aria-label="Finding and interpretation">
          <div class="rw-reading-toolbar"><span>{{ selected.finding ? `Finding ${findings.findIndex(item => item.id === selectedId) + 1} of ${findings.length}` : 'Area with no findings' }}</span><div><button type="button" class="rw-icon-button" aria-label="Previous finding" :disabled="selectedId !== 'b'" @click="selectFinding('a')"><SiteIcon name="arrow-left" /></button><button type="button" class="rw-icon-button" aria-label="Next finding" :disabled="selectedId === 'b'" @click="selectFinding('b')"><SiteIcon name="arrow-right" /></button></div></div>
          <div ref="readingScroll" class="rw-reading-scroll" tabindex="0" aria-label="Evidence and interpretation">
            <div class="rw-finding-meta"><b>{{ currentDecision?.areaId ? areas.find(area => area.id === currentDecision.areaId).code : selected.code }}</b><span>{{ selected.finding ? statusFor(selectedId) : 'No findings' }}</span></div>
            <h3>{{ selected.title }}</h3>
            <p class="rw-summary">{{ currentDecision?.summary || selected.summary }}</p>
            <details v-if="currentDecision?.status === 'corrected'" class="rw-original"><summary>Original assessment</summary><p>{{ selected.summary }}</p></details>
            <details :key="selectedId" class="rw-confidence"><summary>{{ selected.confidence }}</summary><p>{{ selected.confidenceReason }}</p></details>
            <div v-if="selected.question" class="rw-question"><strong>Decision needed</strong><p>{{ selected.question }}</p></div>
            <h4>Evidence and interpretation</h4>
            <button v-for="evidence in assessmentEvidence" :key="evidence.page" type="button" class="rw-passage" :aria-pressed="sourcePage === evidence.page" :aria-label="`Read source page ${evidence.page}`" @click="showSource(evidence.page)"><span>Page {{ evidence.page }}</span><strong>{{ evidence.title }}</strong><span class="rw-passage-reading">{{ evidence.reading }}</span></button>
            <div v-if="currentDecision" class="rw-saved"><strong>{{ statusFor(selectedId) }}</strong><p v-if="currentDecision.note">{{ currentDecision.note }}</p><button type="button" class="rw-text-button" @click="resetDecision">Reset decision</button></div>
          </div>
          <footer v-if="selected.finding" class="rw-decision-dock">
            <button type="button" class="rw-button rw-primary" :disabled="currentDecision?.status === 'confirmed'" @click="saveDecision('confirmed')">Confirm</button>
            <button type="button" class="rw-button" @click="openDialog('correct')">Correct</button>
            <button type="button" class="rw-button" :disabled="currentDecision?.status === 'rejected'" @click="saveDecision('rejected')">Reject</button>
          </footer>
        </section>

        <section v-else ref="explorerScroll" class="rw-explorer-reading" tabindex="0" aria-label="Evidence selection and interpretation">
          <span class="rw-eyebrow">{{ explorerArea.code }}</span><h3>{{ explorerArea.label }}</h3>
          <div class="rw-map-heading"><h4>Evidence by page</h4><select v-model="evidenceFilter" aria-label="Pages to display" @change="filterChanged"><option value="all">All pages ({{ explorerArea.evidence.length }})</option><option value="assessment">Assessment evidence ({{ explorerAssessmentCount }})</option><option value="context">Context ({{ explorerArea.evidence.length - explorerAssessmentCount }})</option></select></div>
          <p class="rw-map-note">Assessment evidence supports the finding. Context shows the other material reviewed.</p>
          <div class="rw-legend"><span><i class="rw-key-assessment" />Assessment evidence</span><span><i />Context</span></div>
          <div class="rw-page-grid" role="group" aria-label="Evidence pages"><button v-for="evidence in explorerEvidence" :key="evidence.page" type="button" :class="{ 'is-assessment': evidence.role === 'assessment' }" :aria-label="`Page ${evidence.page}, ${roleName(evidence.role)}`" :aria-pressed="explorerPage === evidence.page" @click="selectEvidence(evidence.page)">{{ evidence.page }}</button></div>
          <article v-if="activeEvidence" :key="`${explorerId}-${explorerPage}`" class="rw-evidence-detail">
            <header><h4>Page {{ activeEvidence.page }}</h4><span>{{ roleName(activeEvidence.role) }}</span></header>
            <p class="rw-source-section">{{ pages.find(page => page.number === activeEvidence.page).section }}</p>
            <h4>AI interpretation</h4><p>{{ activeEvidence.reading }}</p>
            <details><summary>Source details</summary><strong>{{ activeEvidence.source }}</strong><p>{{ activeEvidence.fact }}</p></details>
          </article>
        </section>

        <section class="rw-source" aria-label="Original report">
          <div class="rw-source-toolbar"><label>Page <input :value="sourcePage" type="number" min="1" :max="pages.length" aria-label="Report page number" @change="changeSourcePage" @keydown.enter="$event.target.blur()" /></label><span>of {{ pages.length }}</span><strong>Original report</strong></div>
          <div ref="sourceScroll" class="rw-source-scroll" tabindex="0" aria-label="Scrollable original report" @scroll.passive="onSourceScroll">
            <article v-for="page in pages" :key="page.number" class="rw-paper" :data-report-page="page.number" :aria-label="`Report page ${page.number}`">
              <header><span>QUARTERLY REVIEW</span><span>APR–JUN 2026</span></header>
              <div class="rw-paper-section">{{ page.section }}</div><h4>{{ page.title }}</h4>
              <p v-for="paragraph in page.paragraphs" :key="paragraph">{{ paragraph }}</p>
              <table><thead><tr><th v-for="column in page.columns" :key="column" scope="col">{{ column }}</th></tr></thead><tbody><tr v-for="(row, rowIndex) in page.rows" :key="rowIndex" :class="{ 'rw-document-mark': page.emphasis === rowIndex && sourcePage === page.number }"><td v-for="(cell, index) in row" :key="index">{{ cell }}</td></tr></tbody></table>
              <p class="rw-paper-note">{{ page.note }}</p><footer><span>Illustrative report</span><span>{{ page.number }}</span></footer>
            </article>
          </div>
        </section>
      </div>
      <p class="rw-announcement" role="status">{{ announcement }}</p>

      <dialog ref="dialog" class="rw-dialog" :aria-label="dialogMode === 'correct' ? 'Correct finding' : 'Review summary'" @close="dialogMode = ''" @click="onDialogBackdrop">
        <header><h3>{{ dialogMode === 'correct' ? 'Correct finding' : 'Review summary' }}</h3><button class="rw-icon-button" type="button" aria-label="Close dialog" @click="$refs.dialog.close()"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg></button></header>
        <form v-if="dialogMode === 'correct'" @submit.prevent="saveCorrection">
          <p>Update the finding and record your judgement.</p>
          <label>Review area<select v-model="draftArea"><option v-for="area in areas" :key="area.id" :value="area.id">{{ area.label }}</option></select></label>
          <label>Finding summary<textarea v-model="draftSummary" required rows="5" /></label>
          <label>Reviewer note <span>(optional)</span><textarea v-model="draftNote" rows="2" /></label>
          <footer><button class="rw-button" type="button" @click="$refs.dialog.close()">Cancel</button><button class="rw-button rw-primary" type="submit" :disabled="!draftSummary.trim()">Save correction</button></footer>
        </form>
        <div v-else-if="dialogMode === 'summary'" class="rw-review-summary">
          <p>{{ counts.reviewed }} of {{ counts.total }} findings reviewed<span v-if="completed"> · Review completed</span></p>
          <dl><div><dt>Accepted</dt><dd>{{ counts.accepted }}</dd></div><div><dt>Rejected</dt><dd>{{ counts.rejected }}</dd></div><div><dt>Awaiting review</dt><dd>{{ counts.total - counts.reviewed }}</dd></div></dl>
          <article v-for="item in findings" :key="item.id"><span>{{ statusFor(item.id) }}<template v-if="decisions[item.id]?.areaId"> · {{ areas.find(area => area.id === decisions[item.id].areaId).label }}</template></span><h4>{{ item.title }}</h4><p>{{ decisions[item.id]?.summary || item.summary }}</p><p v-if="decisions[item.id]?.note">{{ decisions[item.id].note }}</p></article>
        </div>
      </dialog>
    </div>
    <DemoInteractionGate :active="demoActive" label="Explore the review workspace" @activate="activateDemo" @deactivate="leaveDemo" />
    </div>
  </section>
</template>

<script>
import SiteIcon from '../ui/SiteIcon.vue';
import ArrowUpRightIcon from '../ui/ArrowUpRightIcon.vue';
import DemoInteractionGate from '../ui/DemoInteractionGate.vue';
import { reportPages, reviewAreas, reviewCounts } from './reviewExample';

export default {
  name: 'ReportReviewWorkspace',
  components: { SiteIcon, ArrowUpRightIcon, DemoInteractionGate },
  data: () => ({
    pages: reportPages, areas: reviewAreas, selectedId: 'a', exploring: false,
    explorerId: 'a', explorerPage: 3, evidenceFilter: 'all', sourcePage: 3,
    demoActive: false, decisions: {}, completed: false, announcement: '', dialogMode: '', draftSummary: '', draftNote: '', draftArea: 'a',
  }),
  computed: {
    findings() { return this.areas.filter(area => area.finding); },
    selected() { return this.areas.find(area => area.id === this.selectedId); },
    assessmentEvidence() { return this.selected.evidence.filter(item => item.role === 'assessment'); },
    currentDecision() { return this.decisions[this.selectedId]; },
    counts() { return reviewCounts(this.decisions); },
    explorerArea() { return this.areas.find(area => area.id === this.explorerId); },
    explorerEvidence() { return this.explorerArea.evidence.filter(item => this.evidenceFilter === 'all' || item.role === this.evidenceFilter).slice().sort((a, b) => a.page - b.page); },
    explorerAssessmentCount() { return this.explorerArea.evidence.filter(item => item.role === 'assessment').length; },
    activeEvidence() { return this.explorerArea.evidence.find(item => item.page === this.explorerPage); },
  },
  mounted() { this.showSource(3); },
  deactivated() { this.demoActive = false; },
  beforeUnmount() { cancelAnimationFrame(this._sourceFrame); },
  methods: {
    async activateDemo() {
      this.demoActive = true;
      await this.$nextTick();
      (this.$refs.explorerEntry || this.$refs.backToFindings)?.focus({ preventScroll: true });
    },
    leaveDemo() {
      if (this.$refs.dialog?.open) return;
      this.demoActive = false;
    },
    roleName(role) { return role === 'assessment' ? 'Assessment evidence' : 'Context'; },
    statusFor(id) { return { confirmed: 'Confirmed', corrected: 'Corrected', rejected: 'Rejected' }[this.decisions[id]?.status] || 'Awaiting review'; },
    async selectFinding(id) {
      this.selectedId = id;
      await this.$nextTick();
      if (this.$refs.readingScroll) this.$refs.readingScroll.scrollTop = 0;
      this.showSource(this.assessmentEvidence[0].page);
    },
    async openExplorer() {
      this._reviewSourcePage = this.sourcePage;
      this.explorerId = this.selectedId;
      this.explorerPage = this.assessmentEvidence[0].page;
      this.evidenceFilter = 'all';
      this.exploring = true;
      await this.$nextTick();
      this.showSource(this.explorerPage);
      this.$refs.backToFindings.focus({ preventScroll: true });
    },
    async closeExplorer() {
      this.exploring = false;
      await this.$nextTick();
      this.showSource(this._reviewSourcePage || this.assessmentEvidence[0].page);
      this.$refs.explorerEntry.focus({ preventScroll: true });
    },
    selectArea(id) {
      this.explorerId = id;
      this.evidenceFilter = 'all';
      this.selectEvidence(this.explorerArea.evidence[0].page);
    },
    selectEvidence(page) { this.explorerPage = page; this.showSource(page); },
    filterChanged() {
      if (!this.explorerEvidence.some(item => item.page === this.explorerPage)) this.selectEvidence(this.explorerEvidence[0].page);
    },
    async showSource(page) {
      this.sourcePage = Math.max(1, Math.min(this.pages.length, Number(page) || 1));
      await this.$nextTick();
      const viewer = this.$refs.sourceScroll;
      const target = viewer?.querySelector(`[data-report-page="${this.sourcePage}"]`);
      if (target) viewer.scrollTop += target.getBoundingClientRect().top - viewer.getBoundingClientRect().top - 16;
    },
    changeSourcePage(event) { this.showSource(event.target.value); event.target.value = this.sourcePage; },
    onSourceScroll() {
      cancelAnimationFrame(this._sourceFrame);
      this._sourceFrame = requestAnimationFrame(() => {
        const viewer = this.$refs.sourceScroll;
        if (!viewer) return;
        const top = viewer.getBoundingClientRect().top + 36;
        const page = [...viewer.querySelectorAll('[data-report-page]')].find(item => item.getBoundingClientRect().bottom > top);
        if (page) this.sourcePage = Number(page.dataset.reportPage);
      });
    },
    saveDecision(status, extra = {}) {
      this.decisions = { ...this.decisions, [this.selectedId]: { status, ...extra } };
      this.completed = false;
      this.announcement = `${this.selected.title}. ${this.statusFor(this.selectedId)}.`;
    },
    resetDecision() {
      const decisions = { ...this.decisions };
      delete decisions[this.selectedId];
      this.decisions = decisions;
      this.completed = false;
      this.announcement = 'Finding returned to review.';
    },
    async openDialog(mode) {
      this.dialogMode = mode;
      if (mode === 'correct') {
        this.draftSummary = this.currentDecision?.summary || this.selected.summary;
        this.draftNote = this.currentDecision?.note || '';
        this.draftArea = this.currentDecision?.areaId || this.selectedId;
      }
      await this.$nextTick();
      this.$refs.dialog.showModal();
    },
    saveCorrection() {
      if (!this.draftSummary.trim()) return;
      this.saveDecision('corrected', { summary: this.draftSummary.trim(), note: this.draftNote.trim(), areaId: this.draftArea });
      this.$refs.dialog.close();
    },
    completeReview() { if (this.counts.reviewed === this.counts.total) { this.completed = true; this.openDialog('summary'); } },
    onDialogBackdrop(event) {
      if (event.target !== this.$refs.dialog) return;
      const bounds = this.$refs.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.$refs.dialog.close();
    },
  },
};
</script>

<style scoped>
.review-demo { --rw-ink:#292929; --rw-muted:#6b6b6b; --rw-line:#dededc; --rw-bg:#fff; --rw-soft:#f6f6f4; --rw-selected:#eaeae7; --rw-button:#303030; --rw-button-text:#fff; container-type:inline-size; color:var(--rw-ink); font:14px/1.5 'Quattrocento Sans',sans-serif; }
.rw-demo-stage { position:relative; isolation:isolate; padding-bottom:64px; }
.rw-demo-stage:not(.is-active) .rw-shell { pointer-events:none; }
body.dark-mode .review-demo { --rw-ink:#e6e6e2; --rw-muted:#adada8; --rw-line:#484845; --rw-bg:#272725; --rw-soft:#30302e; --rw-selected:#40403c; --rw-button:#e4e4df; --rw-button-text:#252523; }
.rw-shell { position:relative; border:1px solid var(--rw-line); border-radius:10px; background:var(--rw-bg); overflow:hidden; text-align:left; }
.review-demo :is(h3,h4,p) { margin:0; color:inherit; letter-spacing:normal; }
.review-demo h3 { font:600 22px/1.3 'Quattrocento Sans',sans-serif; }
.review-demo h4 { font:600 14px/1.4 'Quattrocento Sans',sans-serif; }
.review-demo :is(button,input,select,textarea) { font:inherit; color:inherit; box-sizing:border-box; }
.review-demo button, .review-demo summary, .review-demo select { cursor:var(--site-interactive-cursor,pointer); }
.review-demo button { transition:background 140ms ease,border-color 140ms ease,color 140ms ease; }
.review-demo button:disabled { opacity:.42; cursor:default; }
.review-demo :is(button,input,select,textarea,summary,[tabindex]):focus-visible { outline:2px solid var(--rw-ink); outline-offset:3px; }
.review-demo :is(svg,.site-icon,.arrow-up-right-icon) { width:17px; height:17px; flex:none; }
.rw-header { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid var(--rw-line); gap:12px; }
.rw-header>div { display:flex; align-items:center; gap:10px; }
.rw-header>span { font-size:12px; color:var(--rw-muted); }
.rw-workspace { display:grid; grid-template-columns:205px minmax(285px, .96fr) minmax(0,1.14fr); height:650px; }
.rw-sidebar { display:flex; flex-direction:column; min-width:0; min-height:0; overflow:auto; background:var(--rw-soft); border-right:1px solid var(--rw-line); }
.rw-report-heading { padding:22px 14px 20px; }
.rw-report-heading h3 { font-size:17px; }
.rw-report-heading p { color:var(--rw-muted); font-size:12px; margin:7px 0 16px; }
.rw-report-heading p>span { display:block; margin-top:2px; }
.rw-explorer-entry { display:flex; align-items:center; justify-content:center; gap:7px; width:100%; padding:10px 7px; border:1px solid var(--rw-line); border-radius:6px; background:var(--rw-bg); font-size:12px!important; font-weight:600!important; }
.rw-explorer-entry svg:last-child { margin-left:auto; width:13px; height:13px; }
.rw-list-label { display:flex; justify-content:space-between; align-items:center; padding:0 15px 9px; gap:8px; font-size:12px; }
.rw-list-label>span { font-size:11px; color:var(--rw-muted); }
.rw-finding-list { padding:0 7px; }
.rw-finding { display:block; width:100%; text-align:left; padding:13px 9px; border:1px solid transparent; background:transparent; border-radius:6px; font-size:13px!important; line-height:1.5!important; margin:0 0 6px; }
.rw-finding[aria-pressed=true] { background:var(--rw-bg); border-color:var(--rw-line); box-shadow:inset 2px 0 var(--rw-ink); }
.rw-finding-meta { display:flex; gap:9px; align-items:center; margin-bottom:9px; font-size:11px; color:var(--rw-muted); }
.rw-finding-meta b { display:grid; place-items:center; background:var(--rw-selected); color:var(--rw-ink); font-size:11px; min-width:22px; height:22px; border-radius:3px; }
.rw-no-findings { margin:9px 14px 18px; font-size:12px; color:var(--rw-muted); }
.rw-no-findings summary { padding:8px 0; }
.rw-no-findings summary>span { margin-left:4px; }
.rw-sidebar-footer { margin-top:auto; padding:14px; display:grid; gap:8px; }
.rw-button { display:inline-flex; justify-content:center; align-items:center; gap:8px; padding:10px 13px; min-height:38px; border:1px solid var(--rw-line); border-radius:5px; background:var(--rw-bg); font-size:13px!important; font-weight:600!important; }
.rw-primary { background:var(--rw-button); border-color:var(--rw-button); color:var(--rw-button-text)!important; }
.rw-reading { display:flex; flex-direction:column; min-width:0; min-height:0; border-right:1px solid var(--rw-line); }
.rw-reading-toolbar,.rw-source-toolbar { display:flex; align-items:center; gap:7px; min-height:47px; padding:7px 15px; box-sizing:border-box; border-bottom:1px solid var(--rw-line); font-size:12px; color:var(--rw-muted); }
.rw-reading-toolbar { justify-content:space-between; }
.rw-reading-toolbar>div { display:flex; gap:4px; }
.rw-icon-button { display:grid; place-items:center; border:1px solid var(--rw-line); background:var(--rw-bg); width:30px; height:30px; padding:5px; border-radius:4px; }
.rw-reading-scroll { min-height:0; flex:1; padding:24px 20px; overflow:auto; overscroll-behavior:contain; scrollbar-width:thin; }
.rw-reading-scroll h3 { margin:13px 0 12px; }
.rw-summary { font-size:14px; line-height:1.65; color:var(--rw-muted)!important; }
.rw-confidence { margin-top:16px; font-size:12px; color:var(--rw-muted); }
.rw-confidence summary { display:list-item; list-style-position:inside; width:fit-content; background:var(--rw-soft); border-radius:4px; padding:5px 8px; }
.rw-confidence>p { padding:10px 0 0; }
.rw-original { margin-top:12px; font-size:12px; color:var(--rw-muted); }
.rw-original p { margin-top:8px; }
.rw-question { margin:18px 0 24px; border-left:2px solid var(--rw-ink); padding:1px 0 1px 12px; font-size:13px; }
.rw-question p { margin-top:5px; }
.rw-reading-scroll>h4 { margin:24px 0 10px; }
.rw-passage { display:block; width:100%; text-align:left; padding:13px 12px; margin:0 0 6px; background:transparent; border:1px solid transparent; border-radius:5px; }
.rw-passage[aria-pressed=true] { background:var(--rw-soft); border-color:var(--rw-line); box-shadow:inset 2px 0 var(--rw-ink); }
.rw-passage>span:first-child { font-size:11px; color:var(--rw-muted); }
.rw-passage>strong { display:block; margin:4px 0 5px; font-size:14px; }
.rw-passage-reading { display:block; color:var(--rw-muted); font-size:13px; line-height:1.6; }
.rw-decision-dock { display:flex; gap:8px; padding:13px 16px; border-top:1px solid var(--rw-line); background:var(--rw-bg); }
.rw-decision-dock>button { flex:1; padding-inline:6px; }
.rw-saved { padding:15px 12px 0; font-size:13px; }
.rw-saved p { margin:6px 0; }
.rw-text-button { display:inline-flex; align-items:center; gap:6px; border:0; background:transparent; padding:6px 0; color:var(--rw-muted)!important; font-size:12px!important; }
.rw-source { display:flex; flex-direction:column; min-width:0; min-height:0; background:var(--rw-soft); }
.rw-source-toolbar label { display:flex; align-items:center; gap:7px; }
.rw-source-toolbar input { width:43px; padding:4px; border:1px solid var(--rw-line); border-radius:3px; text-align:center; background:var(--rw-bg); appearance:textfield; -moz-appearance:textfield; }
.rw-source-toolbar input::-webkit-inner-spin-button { appearance:none; }
.rw-source-toolbar strong { margin-left:auto; font-weight:400; font-size:11px; }
.rw-source-scroll { flex:1; min-height:0; overflow:auto; padding:16px; overscroll-behavior:contain; scrollbar-width:thin; scroll-behavior:auto; }
.rw-paper { padding:26px 24px 20px; min-height:470px; background:#fff; color:#303030; margin-bottom:16px; box-shadow:0 1px 7px #0000000a; font:12px/1.65 'Quattrocento Sans',sans-serif; }
.rw-paper>header { display:flex; justify-content:space-between; gap:8px; border-bottom:1px solid #d5d5d3; padding-bottom:13px; font-size:8px; letter-spacing:.08em; color:#777; }
.rw-paper-section { font-size:10px; color:#777; margin-top:28px; }
.rw-paper>h4 { font:400 23px/1.25 'PT Serif',Georgia,serif; margin:8px 0 20px; }
.rw-paper>p { margin:0 0 14px; }
.rw-paper table { width:100%; border-collapse:collapse; table-layout:fixed; margin:24px 0; font-size:10px; }
.rw-paper th,.rw-paper td { padding:8px 7px; border:1px solid #ddd; text-align:left; vertical-align:top; overflow-wrap:anywhere; }
.rw-paper th { background:#f3f3f1; font-weight:600; }
.rw-paper .rw-document-mark { background:#ecece8; }
.rw-paper-note { font-size:10px; color:#686868!important; }
.rw-paper>footer { display:flex; justify-content:space-between; padding-top:26px; font-size:9px; color:#888; }
.rw-explorer-header { display:flex; align-items:center; gap:24px; min-height:57px; padding:0 18px; border-bottom:1px solid var(--rw-line); }
.rw-explorer-header h3 { font-size:18px; }
.rw-workspace.is-exploring { grid-template-columns:155px minmax(335px,.96fr) minmax(0,1.14fr); height:593px; }
.is-exploring .rw-sidebar { padding-top:22px; }
.rw-area-list { padding:0 8px; }
.rw-area-list .rw-finding b { display:block; margin-bottom:4px; }
.rw-area-list .rw-finding>span { color:var(--rw-muted); font-size:12px; }
.rw-explorer-reading { overflow:auto; min-width:0; padding:25px 22px; border-right:1px solid var(--rw-line); overscroll-behavior:contain; scrollbar-width:thin; }
.rw-eyebrow { color:var(--rw-muted); font-size:11px; }
.rw-explorer-reading>h3 { margin:5px 0 24px; }
.rw-map-heading { display:flex; justify-content:space-between; align-items:center; gap:10px; }
.rw-map-heading select { max-width:175px; min-width:0; border:1px solid var(--rw-line); border-radius:4px; background:var(--rw-bg); padding:6px; font-size:11px; }
.rw-map-note { font-size:12px; line-height:1.6; color:var(--rw-muted)!important; margin:12px 0!important; }
.rw-legend { display:flex; flex-wrap:wrap; gap:12px; font-size:10px; color:var(--rw-muted); margin:15px 0 10px; }
.rw-legend>span { display:flex; align-items:center; gap:5px; }
.rw-legend i { width:8px; height:8px; background:var(--rw-selected); border:1px solid var(--rw-line); border-radius:2px; }
.rw-legend .rw-key-assessment { background:var(--rw-button); border-color:var(--rw-button); }
.rw-page-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(37px,1fr)); gap:6px; padding:3px; }
.rw-page-grid button { min-height:36px; border:1px solid var(--rw-line); border-radius:4px; background:var(--rw-selected); font-size:12px; }
.rw-page-grid .is-assessment { background:var(--rw-button); color:var(--rw-button-text); border-color:var(--rw-button); }
.rw-page-grid button[aria-pressed=true] { outline:1px solid var(--rw-ink); outline-offset:2px; }
.rw-evidence-detail { margin-top:30px; }
.rw-evidence-detail>header { display:flex; align-items:center; justify-content:space-between; gap:8px; }
.rw-evidence-detail>header h4 { font-size:18px; }
.rw-evidence-detail>header span { font-size:10px; padding:3px 6px; border-radius:3px; background:var(--rw-soft); color:var(--rw-muted); }
.rw-source-section { font-size:12px; color:var(--rw-muted)!important; margin:6px 0 24px!important; }
.rw-evidence-detail>p:not(.rw-source-section) { margin:9px 0 17px; color:var(--rw-muted); line-height:1.65; font-size:13px; }
.rw-evidence-detail details { font-size:12px; color:var(--rw-muted); }
.rw-evidence-detail summary { padding:8px 0; }
.rw-evidence-detail details p { margin-top:6px; }
.rw-announcement { position:absolute; width:1px; height:1px; overflow:hidden; clip-path:inset(50%); }
.rw-dialog { box-sizing:border-box; width:min(520px,calc(100vw - 36px)); max-height:80svh; overflow:auto; padding:25px; border:1px solid var(--rw-line); border-radius:9px; background:var(--rw-bg); color:var(--rw-ink); box-shadow:0 24px 90px #0003; font:14px/1.5 'Quattrocento Sans',sans-serif; }
.rw-dialog::backdrop { background:#0007; backdrop-filter:blur(4px); }
.rw-dialog>header { display:flex; align-items:center; justify-content:space-between; gap:20px; margin-bottom:18px; }
.rw-dialog svg { fill:none; stroke:currentColor; stroke-width:1.6; }
.rw-dialog form>p { color:var(--rw-muted); margin-bottom:20px; }
.rw-dialog label { display:block; margin-bottom:18px; font-weight:600; }
.rw-dialog label span { font-weight:400; color:var(--rw-muted); }
.rw-dialog textarea { display:block; width:100%; margin-top:7px; padding:10px; resize:vertical; border:1px solid var(--rw-line); border-radius:4px; background:var(--rw-soft); font-weight:400; }
.rw-dialog select { display:block; width:100%; margin-top:7px; padding:10px; border:1px solid var(--rw-line); border-radius:4px; background:var(--rw-soft); }
.rw-dialog form>footer { display:flex; justify-content:flex-end; gap:10px; }
.rw-review-summary>p { color:var(--rw-muted); }
.rw-review-summary dl { display:flex; gap:28px; padding:20px 0; }
.rw-review-summary dt { color:var(--rw-muted); font-size:12px; }
.rw-review-summary dd { margin:5px 0 0; font-size:24px; }
.rw-review-summary article { margin:0 0 25px; }
.rw-review-summary article>span { font-size:11px; color:var(--rw-muted); }
.rw-review-summary article h4 { margin:5px 0 8px; font-size:16px; }
.rw-review-summary article p { color:var(--rw-muted); font-size:13px; }
@media (hover:hover) { .review-demo button:not(:disabled):hover { background:var(--rw-selected); } .review-demo .rw-primary:not(:disabled):hover,.review-demo .rw-page-grid .is-assessment:hover { background:var(--rw-button); filter:brightness(.85); } .review-demo summary:hover { color:var(--rw-ink); } }
@container (max-width:900px) {
  .rw-workspace,.rw-workspace.is-exploring { grid-template-columns:minmax(0,1fr) minmax(0,1.08fr); grid-template-rows:auto minmax(0,1fr); height:760px; }
  .rw-sidebar { grid-column:1/-1; display:grid; grid-template-columns:175px minmax(0,1fr); border-right:0; border-bottom:1px solid var(--rw-line); padding:10px; overflow:visible; }
  .rw-report-heading { padding:4px 9px 4px 2px; grid-row:1/3; }
  .rw-report-heading p { margin:4px 0 8px; font-size:11px; }
  .rw-report-heading p>span { display:inline; margin-left:5px; }
  .rw-list-label { display:none; }
  .rw-finding-list { display:flex; gap:5px; }
  .rw-finding-list .rw-finding { flex:1; margin:0; padding:9px; font-size:12px!important; }
  .rw-finding-meta { margin-bottom:6px; }
  .rw-sidebar-footer { grid-column:1/-1; display:flex; justify-content:flex-end; padding:8px 0 0; gap:8px; }
  .rw-sidebar-footer .rw-button { padding:6px 9px; min-height:32px; font-size:11px!important; }
  .rw-no-findings { margin:4px 10px 0; }
  .rw-no-findings .rw-finding { margin-bottom:0; }
  .is-exploring .rw-sidebar { padding:8px 12px; display:block; }
  .rw-area-list { display:flex; gap:8px; padding:0; }
  .rw-area-list .rw-finding { margin:0; display:flex; align-items:center; gap:8px; padding:8px; }
  .rw-area-list .rw-finding b { margin:0; }
  .rw-explorer-reading { padding:20px 16px; }
  .rw-map-heading { flex-wrap:wrap; }
  .rw-paper { padding:22px 18px; }
}
@container (max-width:580px) {
  .rw-header { padding:12px; }
  .rw-header>span { font-size:11px; }
  .rw-workspace,.rw-workspace.is-exploring { height:auto; grid-template-columns:minmax(0,1fr); grid-template-rows:auto auto auto; }
  .rw-sidebar { grid-template-columns:minmax(0,1fr); padding:12px; }
  .rw-report-heading { grid-row:auto; display:grid; grid-template-columns:1fr auto; align-items:center; column-gap:8px; margin-bottom:12px; padding:0; }
  .rw-report-heading h3 { font-size:15px; }
  .rw-report-heading p { margin:3px 0 0; }
  .rw-explorer-entry { grid-column:2; grid-row:1/3; width:auto; font-size:11px!important; padding:9px; }
  .rw-explorer-entry>svg:first-child { display:none; }
  .rw-finding-list { padding:0; }
  .rw-finding-list .rw-finding { padding:8px; }
  .rw-no-findings { margin:4px 0 0; }
  .rw-reading { height:480px; border-right:0; }
  .rw-reading-scroll { padding:20px 16px; }
  .rw-source { height:430px; border-top:1px solid var(--rw-line); }
  .rw-source-scroll { padding:14px; }
  .rw-paper { max-width:420px; margin-inline:auto; padding:25px; }
  .rw-explorer-header { gap:16px; padding:8px 12px; flex-wrap:wrap; }
  .rw-explorer-header h3 { font-size:17px; }
  .rw-explorer-reading { height:430px; box-sizing:border-box; border-right:0; padding:20px 16px; }
  .rw-area-list .rw-finding { gap:5px; }
  .rw-area-list .rw-finding>span { font-size:11px; }
}
@media (prefers-reduced-motion:reduce) { .review-demo button { transition:none; } }
</style>
